import { Link } from 'react-router'
import { SITE_NAME } from '#app/utils/branding.ts'
import { type Route } from './+types/tos.ts'

const LAST_UPDATED = 'March 1, 2026'
const CONTACT_EMAIL = 'support@ikonexamen.com'

export const meta: Route.MetaFunction = () => [
	{ title: `Terms of Service - ${SITE_NAME}` },
	{
		name: 'description',
		content: `Read the terms that govern access to and use of ${SITE_NAME}.`,
	},
]

export default function TermsOfServiceRoute() {
	return (
		<main className="bg-cathedral-page min-h-screen px-6 py-16 md:py-24">
			<article className="bg-cathedral-card mx-auto w-full max-w-4xl rounded-xl border p-8 md:p-12">
				<header className="border-cathedral-gold/20 border-b pb-6">
					<p className="cathedral-heading text-cathedral-gold text-xs tracking-[0.14em] uppercase">
						{SITE_NAME}
					</p>
					<h1 className="cathedral-heading text-cathedral-dark mt-2 text-4xl font-semibold tracking-wide md:text-5xl">
						Terms of Service
					</h1>
					<p className="cathedral-body text-cathedral-brown mt-3 text-sm">
						Last updated: {LAST_UPDATED}
					</p>
				</header>

				<div className="cathedral-body text-cathedral-brown mt-8 space-y-8 text-base leading-relaxed md:text-lg">
					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							1. Acceptance of Terms
						</h2>
						<p className="mt-3">
							By accessing or using {SITE_NAME}, you agree to these Terms of
							Service and our{' '}
							<Link
								to="/privacy"
								className="text-cathedral-gold hover:text-cathedral-gold-light underline"
							>
								Privacy Policy
							</Link>
							. If you do not agree, do not use the service.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							2. Eligibility and Accounts
						</h2>
						<ul className="mt-3 list-disc space-y-2 pl-5">
							<li>
								You must provide accurate account information and keep it
								updated.
							</li>
							<li>
								You are responsible for safeguarding your account credentials
								and for all activity under your account.
							</li>
							<li>You must promptly notify us of unauthorized account use.</li>
						</ul>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							3. Service Description
						</h2>
						<p className="mt-3">
							{SITE_NAME} provides a formation instrument workflow for spiritual
							self-knowledge and growth, including scoring and AI-assisted
							narrative interpretation. Assessment output is informational only
							and is not psychological, medical, clinical, legal, or financial
							advice or diagnosis.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							4. Purchases, Billing, and Refunds
						</h2>
						<ul className="mt-3 list-disc space-y-2 pl-5">
							<li>
								Payments are processed by Stripe. By purchasing, you authorize
								the applicable payment charge.
							</li>
							<li>
								Prices, features, and tier availability may change at any time.
							</li>
							<li>
								Unless required by law or explicitly stated otherwise, purchases
								are non-refundable once assessment access is granted.
							</li>
						</ul>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							5. License and Acceptable Use
						</h2>
						<p className="mt-3">
							Subject to these Terms, we grant you a limited, non-exclusive,
							non-transferable, revocable license to use the service for
							personal, non-commercial use. You agree not to misuse the service,
							including by attempting unauthorized access, interfering with
							security, reverse engineering, scraping at abusive volumes, or
							violating applicable law.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							6. Intellectual Property
						</h2>
						<p className="mt-3">
							The service, including software, design, branding, and content
							(excluding your submitted data), is owned by us or our licensors
							and protected by intellectual property laws. These Terms do not
							transfer ownership rights to you.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							7. User Content
						</h2>
						<p className="mt-3">
							You retain rights to content you submit. You grant us a license to
							host, process, and use submitted content solely to provide,
							maintain, and improve the service, consistent with the Privacy
							Policy.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							8. Disclaimers
						</h2>
						<p className="mt-3">
							To the maximum extent permitted by law, the service is provided
							&quot;as is&quot; and &quot;as available&quot; without warranties
							of any kind, express or implied, including merchantability,
							fitness for a particular purpose, and non-infringement.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							9. Limitation of Liability
						</h2>
						<p className="mt-3">
							To the extent permitted by law, we are not liable for indirect,
							incidental, special, consequential, or punitive damages, or for
							loss of profits, data, goodwill, or business interruption arising
							from use of the service.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							10. Indemnification
						</h2>
						<p className="mt-3">
							You agree to indemnify and hold us harmless from claims,
							liabilities, damages, and expenses arising from your misuse of the
							service, violation of these Terms, or infringement of rights of
							others.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							11. Suspension and Termination
						</h2>
						<p className="mt-3">
							We may suspend or terminate access if you violate these Terms,
							pose a security risk, or where required by law. You may stop using
							the service at any time.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							12. Changes to Terms
						</h2>
						<p className="mt-3">
							We may update these Terms from time to time. Continued use after
							changes become effective constitutes acceptance of the updated
							Terms.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							13. Contact
						</h2>
						<p className="mt-3">
							For questions about these Terms, contact{' '}
							<a
								href={`mailto:${CONTACT_EMAIL}`}
								className="text-cathedral-gold hover:text-cathedral-gold-light underline"
							>
								{CONTACT_EMAIL}
							</a>
							.
						</p>
					</section>
				</div>

				<footer className="border-cathedral-gold/20 mt-10 border-t pt-6">
					<div className="cathedral-body text-cathedral-brown flex flex-wrap items-center gap-4 text-sm">
						<Link
							to="/privacy"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Privacy Policy
						</Link>
						<Link
							to="/"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Back to Home
						</Link>
					</div>
				</footer>
			</article>
		</main>
	)
}
